import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import * as React from 'react';
import { matchRoutes, NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Store } from 'redux';

import { useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { GlobalState } from '@/redux/store';
import { LoadableRouteConfig } from '@/routes';
import { Basename } from '@/types/Basename';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';

import { getBasename } from './getBasename';
import ScrollMemory from './ScrollMemory';

/**
 * Fetches any route preload data while changing to that route on the client-side
 * @see /src/server/ssr.js for server-side implementation
 */

const addTrailingSlashIfNeeded = (path: string) => (path === '' || path === Basename.ENGB ? path + '/' : path);

type RouteDataLoaderParams = {
    children: React.ReactNode;
    routes: LoadableRouteConfig[];
    store: Store<GlobalState>;
};

const RouteDataLoader = ({ children, routes, store }: RouteDataLoaderParams) => {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const currRouterLocation = useLocation();
    const currentLocationKey = currRouterLocation.key;
    const previousLocationKey = usePrevious(currentLocationKey);
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const wasLoggedIn = usePrevious(userLoggedIn);
    const { variants: amplitudeExperimentFlags } = useAmplitudeExperiments();
    const isInitialRender = React.useRef(true);
    const featureFlags = useFeatureFlags();

    const loadData = React.useCallback(() => {
        // Fetch preload data for current route
        const basename = getBasename();

        // Get all routes that match current location, basename isn't added since currRouterLocation comes from useLocation()
        const branches = matchRoutes(routes, currRouterLocation);

        let dataLoaderContext: { url?: string } = {};

        // Run each matched route's loadData function if it exists
        branches.forEach((branch) => {
            const { params, route } = branch;

            if (route.loadData) {
                const routeParams: DataLoaderParams = {
                    amplitudeExperimentFlags,
                    featureFlags,
                    location: {
                        ...currRouterLocation,
                        pathname: addTrailingSlashIfNeeded(currRouterLocation.pathname),
                    },
                    match: { params: params },
                    redirect: (route: string) => {
                        dataLoaderContext.url = addTrailingSlashIfNeeded(`${basename}${route}`.replace('//', '/'));
                        return navigate(dataLoaderContext.url);
                    },
                    routerState: currRouterLocation?.state ?? {},
                    store,
                };
                route.loadData(routeParams);
            }
        });

        // handle any redirects inside the page loadData function
        // unsure how this triggers in this flow, the set in redirect func above
        // only happens when it is called.
        if (dataLoaderContext.url) {
            return navigate(dataLoaderContext.url);
        }
    }, [routes, currRouterLocation, amplitudeExperimentFlags, store, navigate, featureFlags]);

    React.useEffect(() => {
        // Load the page data if the location changes and the page is client side
        if (Boolean(previousLocationKey) && previousLocationKey !== currentLocationKey) {
            loadData();
            /**
             * Handle back/forward navigation (POP navigation type)
             *
             * If user refreshes current page and goes back, we should refresh the data
             * to prevent bfcache issues.
             *
             * A POP indicates a change to an arbitrary index in the history stack, such
             * as a back or forward navigation. It does not describe the direction of the
             * navigation, only that the current index changed.
             *
             * Note: This is the default action for newly created history objects.
             *
             * @see https://developer.mozilla.org/en-US/docs/Glossary/bfcache
             */
        } else if (navigationType === NavigationType.Pop && previousLocationKey === currentLocationKey) {
            if (isInitialRender.current) {
                if (process.env.NODE_ENV === 'development') {
                    // Because of strict mode, the page is rendered twice, so we need to wait for the second render to finish
                    // before setting isInitialRender to false
                    window.requestAnimationFrame(() => {
                        isInitialRender.current = false;
                    });
                } else {
                    isInitialRender.current = false;
                }
                return;
            }
            loadData();
        }
    }, [currentLocationKey, previousLocationKey, loadData, navigationType]);

    React.useEffect(() => {
        // Reload the page data if the user logs in
        if (typeof wasLoggedIn !== 'undefined' && userLoggedIn && Boolean(wasLoggedIn) !== userLoggedIn) {
            loadData();
        }
    }, [userLoggedIn, wasLoggedIn, loadData]);

    return (
        <>
            <ScrollMemory />
            {children}
        </>
    );
};

export default RouteDataLoader;
